import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import InfoIcon from '@mui/icons-material/Info';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import openDatePicker from '../common/util/openDatePicker';
import { useLocalization } from '../common/components/LocalizationProvider';
import EditItemView from './components/EditItemView';
import EditAttributesAccordion from './components/EditAttributesAccordion';
import { useTranslation } from '../common/components/LocalizationProvider';
import useUserAttributes from '../common/attributes/useUserAttributes';
import SettingsMenu from './components/SettingsMenu';
import useCommonUserAttributes from '../common/attributes/useCommonUserAttributes';
import useSettingsStyles from './common/useSettingsStyles';
import { useAdministrator, useManager } from '../common/util/permissions';
import { sessionActions } from '../store';
import useMapStyles from '../map/core/useMapStyles';

const UserPage = () => {
  const { classes } = useSettingsStyles();
  const dispatch = useDispatch();
  const t = useTranslation();
  const theme = useTheme();
  const { htmlLang } = useLocalization();

  const expirationRef = useRef(null);

  const isAdmin = useAdministrator();
  const isManager = useManager();

  const currentUser = useSelector((state) => state.session.user);
  const openIdForced = useSelector((state) => state.session.server.openIdForce);

  const commonUserAttributes = useCommonUserAttributes(t);
  const userAttributes = useUserAttributes(t);
  const mapStyles = useMapStyles();

  const [item, setItem] = useState(null);

const validate = () => item && item.name && item.email;

  const onItemSaved = (result) => {
    if (result.id === currentUser.id) {
      dispatch(sessionActions.updateUser(result));
    }
  };

  return (

    <EditItemView
      endpoint="users"
      item={item}
      setItem={setItem}
      defaultItem={isAdmin ? { deviceLimit: -1, userLimit: -1 } : {}}
      validate={validate}
      onItemSaved={onItemSaved}
      menu={<SettingsMenu />}
      breadcrumbs={['settingsTitle', 'settingsUser']}
    >
      {item && (
        <>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <NewReleasesIcon color="primary" />
                <Typography variant="subtitle1">{t('sharedRequired')}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                width="100%"
              >
                <Box
                  display="flex"
                  width="100%"
                  gap={2}
                  sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                >
                  {/* Nome */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item.name || ''}
                      onChange={(event) => setItem({ ...item, name: event.target.value })}
                      label={t('sharedName')}
                    />
                  </Box>

                  {/* Email */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item.email || ''}
                      onChange={(event) => setItem({ ...item, email: event.target.value })}
                      label={t('userEmail')}
                    />
                  </Box>

                  {/* Senha */}
                  {!openIdForced && (
                    <Box flex={1} minWidth={0}>
                      <TextField
                        fullWidth
                        type="password"
                        onChange={(event) => setItem({ ...item, password: event.target.value })}
                        label={t('userPassword')}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <InfoIcon color="primary" />
                <Typography variant="subtitle1">
                  {t('sharedExtra')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box display="flex" flexDirection="column" gap={3}>
                {/* Linha 1 - Telefone e Mapa */}
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item.phone || ''}
                      onChange={(event) => setItem({ ...item, phone: event.target.value })}
                      label={t('sharedPhone')}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <FormControl
                      fullWidth
                      sx={{
                        mt: { xs: 2, sx: '2px' }, // 🔧 Ajuste sutil de alinhamento vertical
                        '& .MuiInputBase-root': {
                          alignItems: 'center', // Corrige baseline diferente entre TextField e Select
                        },
                      }}
                    >
                      <InputLabel>{t('mapDefault')}</InputLabel>
                      <Select
                        label={t('mapDefault')}
                        value={item.map || 'openFreeMap'}
                        onChange={(event) => setItem({ ...item, map: event.target.value })}
                      >
                        {mapStyles.filter((s) => s.available).map((style) => (
                          <MenuItem key={style.id} value={style.id}>
                            {style.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      type="number"
                      value={item.latitude || 0}
                      onChange={(event) => setItem({ ...item, latitude: Number(event.target.value) })}
                      label={t('positionLatitude')}
                      disabled={!isAdmin}
                    />
                  </Box>
                </Box>

                {/* Linha 2 - Longitude, Zoom, Device Limit */}
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      type="number"
                      value={item.longitude || 0}
                      onChange={(event) => setItem({ ...item, longitude: Number(event.target.value) })}
                      label={t('positionLongitude')}
                      disabled={!isAdmin}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      type="number"
                      value={item.zoom || 0}
                      onChange={(event) => setItem({ ...item, zoom: Number(event.target.value) })}
                      label={t('serverZoom')}
                      disabled={!isAdmin}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      type="number"
                      value={item.deviceLimit || 0}
                      onChange={(event) => setItem({ ...item, deviceLimit: Number(event.target.value) })}
                      label={t('userDeviceLimit')}
                      disabled={!isAdmin}
                    />
                  </Box>
                </Box>

                {/* Linha 3 - User Limit e Expiration Time */}
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      type="number"
                      value={item.userLimit || 0}
                      onChange={(event) => setItem({ ...item, userLimit: Number(event.target.value) })}
                      label={t('userUserLimit')}
                      disabled={!isAdmin}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item.expirationTime ? new Date(item.expirationTime).toISOString().slice(0, 10) : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          setItem({ ...item, expirationTime: new Date(e.target.value).toISOString() });
                        } else {
                          setItem({ ...item, expirationTime: null });
                        }
                      }}
                      label={t('userExpirationTime')}
                      disabled={!isManager}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        inputProps: { type: 'date', lang: htmlLang },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" edge="end" onClick={() => openDatePicker(expirationRef.current)}>
                              <CalendarTodayIcon sx={{ color: theme.palette.action.active }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      inputRef={expirationRef}
                      className={classes.dateInput}
                    />
                  </Box>
                  <Box flex={1} minWidth={0} />
                </Box>

                {/* Linha 4 - Checkboxes */}
<Box
  display="flex"
  flexWrap="wrap"
  gap={2}
  sx={{
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: 'center',
  }}
>
  <FormControlLabel
    control={
      <Checkbox
        checked={item.administrator || false}
        onChange={(e) => setItem({ ...item, administrator: e.target.checked })}
      />
    }
    label={t('userAdmin')}
    disabled={!isAdmin}
  />

  <FormControlLabel
    control={
      <Checkbox
        checked={item.disabled || false}
        onChange={(e) => setItem({ ...item, disabled: e.target.checked })}
      />
    }
    label={t('sharedDisabled')}
    disabled={!isManager}
  />

  <FormControlLabel
    control={
      <Checkbox
        checked={item.readonly || false}
        onChange={(e) => setItem({ ...item, readonly: e.target.checked })}
      />
    }
    label={t('serverReadonly')}
    disabled={!isAdmin}
  />

  <FormControlLabel
    control={
      <Checkbox
        checked={item.limitCommands || false}
        onChange={(e) => setItem({ ...item, limitCommands: e.target.checked })}
      />
    }
    label={t('userLimitCommands')}
    disabled={!isAdmin}
  />
</Box>

              </Box>
            </AccordionDetails>
          </Accordion>

          <EditAttributesAccordion
            attributes={item.attributes}
            setAttributes={(attributes) => setItem({ ...item, attributes })}
            definitions={{ ...commonUserAttributes, ...userAttributes }}
          />
        </>
      )}
    </EditItemView>
  );
};

export default UserPage;
